import { store } from 'lib';
import { AuthEntity } from '_entities';
import { AUTH_TOKEN } from '_constants';
import types from '_entities/Auth';
import { isNull } from 'lodash';
import idbService from '../idb';

const { setToken, getProfileData, startWithoutToken, getModularityPermissions } = AuthEntity.actions;

export const saveToken = (token) => {
  localStorage.setItem(AUTH_TOKEN, token);
  store.dispatch(setToken(token));
};

export const removeToken = () => {
  localStorage.removeItem(AUTH_TOKEN);
  store.dispatch({ type: types.types.LOG_OUT });
  // LOG_OUT clears modularityPermissions on purpose -- the API computes that list per user, so the
  // signed-in user's feature flags must not survive into the logged-out state. But logging out is a
  // client-side history.push, so App.js's mount fetch never re-runs and the list stayed empty:
  // `new_ui_2026_06` vanished and the login page dropped to the old design (and lost anything else
  // the account exposes anonymously). Re-fetch so the anonymous set replaces the user's one.
  // Best-effort: a failure here must not break signing out.
  store.dispatch(getModularityPermissions()).catch(() => {});
};

export const manageToken = (token = null) => {
  if (typeof token === 'string' && token.length > 1) {
    saveToken(token);
    return;
  }
  removeToken();
};

export const startApp = async () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('auth_token') || getToken() || null;
  if (isNull(token)) {
    store.dispatch(startWithoutToken());
    return;
  }
  manageToken(token);
  try {
    const profileInfo = await store.dispatch(getProfileData());
    // Mirror every successful profile fetch into IDB so that an offline
    // refresh can hydrate the app without needing the user to have hit
    // "Add to offline" first. Best-effort — a failure to persist shouldn't
    // invalidate the login.
    try {
      await idbService.putProfile(profileInfo);
    } catch (e) {
      // swallow
    }
  } catch (error) {
    // Only a genuine auth rejection should end the session. A 401/403 means
    // the token is invalid (the client middleware has already cleared it), so
    // log out. ANY other failure — no response (offline / server unreachable),
    // timeout, 5xx — must NOT log the user out: the token is still valid, we
    // just couldn't load the profile this time.
    const status = error?.response?.status;
    const isAuthError = status === 401 || status === 403;

    if (!isAuthError) {
      // Hydrate the profile from the last successful fetch so an offline (or
      // server-unreachable) refresh keeps the user in the app. We try this
      // unconditionally rather than gating on navigator.onLine / the offline-
      // mode flag: the failed request is itself proof we couldn't reach the
      // server, and those signals are unreliable on a fresh reload (the
      // offline-mode flag is still loading from IDB when startApp runs, and
      // navigator.onLine reports a LAN link, not real connectivity). That gate
      // was the reason a refresh on the dev env logged the user out.
      try {
        const db = await idbService.initiateIDB();
        const cachedProfile = await db.transaction('profile').objectStore('profile').get('profile');
        if (cachedProfile?.data?.[0]) {
          store.dispatch({
            type: types.types.GET_PROFILE_DATA_SUCCESS,
            result: cachedProfile,
          });
          return;
        }
      } catch (e) {
        // IDB unreadable — fall through and start the app without a profile.
      }
      // No cached profile to fall back on, but the token may still be valid.
      // Let the app boot WITHOUT discarding the token so the next online load
      // signs the user straight back in instead of forcing a re-login.
      store.dispatch(startWithoutToken());
      return;
    }

    removeToken();
    store.dispatch(startWithoutToken());
  }
};

export const getToken = () => localStorage.getItem(AUTH_TOKEN) || null;

export default {
  removeToken,
  manageToken,
  saveToken,
  getToken,
  startApp,
};
